import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { RefreshIcon, SearchIcon, CloseIcon, TrashIcon, FileIcon } from './Icons';
import Pagination from './Pagination';

const DEFAULT_PAGE_SIZE = 50;
// Same silent-background-refresh pattern as DailyOrders.js/AbandonedCarts.js
// — more than one agent can be adding orders here, so poll rather than
// requiring a manual refresh to see what a teammate just logged.
const AUTO_REFRESH_MS = 10000;
// Mirrors INTERNATIONAL_ORDER_MAX_IMAGE_BYTES in server/ivr/routes.js —
// checked here too so an oversized file is rejected instantly.
const MAX_BILL_IMAGE_BYTES = 25 * 1024 * 1024;

// The bill field accepts either a photo or a scanned/exported PDF — a real
// bill often only exists as one or the other. bill_image_url has no
// separate content-type column (see server/ivr/db.js), so PDF-ness is
// inferred from the URL's extension wherever the bill needs to render
// differently (thumbnail vs. a plain link).
const isPdfUrl = (url) => Boolean(url) && /\.pdf($|\?)/i.test(url);
const isPdfFile = (file) => Boolean(file) && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name || ''));
// How long to wait after the agent stops typing a product search before
// querying the ERP catalog - avoids firing a request on every keystroke.
const PRODUCT_SEARCH_DEBOUNCE_MS = 300;
// Survives an accidental sidebar-nav click while the Add Order form is open
// — App.js fully unmounts this page's component tree on tab switch, which
// would otherwise silently wipe out whatever the agent had typed so far.
// The bill image (a File) can't be serialized into sessionStorage, so it's
// intentionally left out of the draft — only re-picking it survives.
const DRAFT_STORAGE_KEY = 'internationalOrderDraft';

function loadOrderDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveOrderDraft(draft) {
  try {
    sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // sessionStorage unavailable (e.g. private browsing) — draft just won't survive a remount.
  }
}

function clearOrderDraft() {
  try {
    sessionStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function formatDateTime(raw) {
  if (!raw) return '-';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// Reconstructs the line-item picker's [{ name, qty }] shape from the joined
// "qty x name, qty x name" string a product was saved as (see
// AddInternationalOrderModal's products state) - lets EditInternationalOrderModal
// reopen the same picker UI instead of a plain text field.
function parseProductString(raw) {
  if (!raw) return [];
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = part.match(/^(\d+)\s*x\s*(.+)$/i);
      return match ? { qty: Number(match[1]) || 1, name: match[2].trim() } : { qty: 1, name: part };
    });
}

function formatAmount(amount) {
  if (!amount) return '-';
  const value = Number(amount);
  return Number.isNaN(value) ? amount : value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

// "Add Order" form — a plain agent-entered log with no ERP/Shopify source
// (see server/ivr/db.js's ivr_international_orders), so every field here is
// typed in by hand rather than looked up. The bill image (if any) is
// uploaded first, same two-step upload-then-submit flow as
// OrderQueryForm.js's attachment.
function AddInternationalOrderModal({ onClose, onSubmit }) {
  const { authFetch } = useAuth();
  const draft = loadOrderDraft();
  const [phone, setPhone] = useState(draft?.phone || '');
  const [name, setName] = useState(draft?.name || '');
  const [country, setCountry] = useState(draft?.country || '');
  const [totalAmount, setTotalAmount] = useState(draft?.totalAmount || '');
  // Line items built up via the ERP catalog picker below - [{ name, qty }].
  // Joined into a single "qty x name, qty x name" string on submit, since
  // ivr_international_orders.product is still a plain text column.
  const [products, setProducts] = useState(draft?.products || []);
  const [productSearch, setProductSearch] = useState('');
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [productQty, setProductQty] = useState(1);
  const [awbNumber, setAwbNumber] = useState(draft?.awbNumber || '');
  const [courierName, setCourierName] = useState(draft?.courierName || '');
  const [trackingLink, setTrackingLink] = useState(draft?.trackingLink || '');
  const [billImage, setBillImage] = useState(null); // File — not persisted, see DRAFT_STORAGE_KEY note above
  const [billImagePreviewUrl, setBillImagePreviewUrl] = useState('');
  const [imageError, setImageError] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Keep the draft in sync so it's there if this component gets unmounted
  // by a sidebar tab switch (see DRAFT_STORAGE_KEY above) and re-mounted later.
  useEffect(() => {
    saveOrderDraft({ phone, name, country, totalAmount, products, awbNumber, courierName, trackingLink });
  }, [phone, name, country, totalAmount, products, awbNumber, courierName, trackingLink]);

  // Only an explicit X/Cancel click should discard the draft and close —
  // an accidental click on the backdrop used to do both instantly.
  const handleClose = () => {
    clearOrderDraft();
    onClose();
  };

  // Live object URL for the picked-but-not-yet-uploaded image, so "View"
  // works instantly — revoked whenever the picked file changes or unmounts.
  useEffect(() => {
    if (!billImage) {
      setBillImagePreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(billImage);
    setBillImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [billImage]);

  const handleImageChange = (event) => {
    const picked = event.target.files?.[0] || null;
    event.target.value = ''; // allow re-picking the same file after removing it
    if (!picked) return;
    if (!picked.type.startsWith('image/') && !isPdfFile(picked)) {
      setImageError('Bill must be an image or a PDF file');
      return;
    }
    if (picked.size > MAX_BILL_IMAGE_BYTES) {
      setImageError('File is too large — the limit is 25 MB');
      return;
    }
    setImageError('');
    setBillImage(picked);
  };

  // Queries the ERP's product catalog (not scoped to this customer/phone at
  // all - see GET /api/ivr/international-orders/products) as the agent
  // types, so they pick a real product instead of typing one out freeform.
  useEffect(() => {
    const term = productSearch.trim();
    if (!term) {
      setProductSuggestions([]);
      setProductSearchLoading(false);
      return undefined;
    }
    setProductSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await authFetch(`/api/ivr/international-orders/products?search=${encodeURIComponent(term)}`);
        const body = await response.json();
        setProductSuggestions(response.ok ? (body.products || []) : []);
      } catch {
        setProductSuggestions([]);
      } finally {
        setProductSearchLoading(false);
      }
    }, PRODUCT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [productSearch, authFetch]);

  const handleSelectProductSuggestion = (suggestion) => {
    setProductSearch(suggestion.name);
    setProductSuggestions([]);
  };

  // Falls back to whatever's typed even if it doesn't match a catalog
  // suggestion (e.g. the ERP lookup failed, or this product genuinely isn't
  // in the catalog) - the picker is a convenience, not a hard requirement.
  const handleAddProduct = () => {
    const trimmedName = productSearch.trim();
    if (!trimmedName) return;
    setProducts((prev) => [...prev, { name: trimmedName, qty: productQty || 1 }]);
    setProductSearch('');
    setProductQty(1);
    setProductSuggestions([]);
  };

  const handleRemoveProduct = (index) => {
    setProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!phone.trim()) {
      setError('Phone number is required.');
      return;
    }
    if (imageError) {
      setError(imageError);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      let billImageUrl;
      if (billImage) {
        const formData = new FormData();
        formData.append('file', billImage);
        const uploadResponse = await authFetch('/api/ivr/international-orders/attachment', {
          method: 'POST',
          body: formData,
        });
        const uploadBody = await uploadResponse.json();
        if (!uploadResponse.ok) throw new Error(uploadBody?.error || 'Bill image upload failed');
        billImageUrl = uploadBody.url;
      }
      await onSubmit({
        phone: phone.trim(),
        name: name.trim(),
        country: country.trim(),
        totalAmount: totalAmount.trim(),
        product: products.map((p) => `${p.qty}x ${p.name}`).join(', '),
        billImageUrl,
        awbNumber: awbNumber.trim(),
        courierName: courierName.trim(),
        trackingLink: trackingLink.trim(),
      });
      clearOrderDraft();
    } catch (err) {
      setError(err.message || 'Failed to save this order.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay-center">
      <div className="modal-panel modal-panel-narrow">
        <div className="modal-header">
          <div>
            <h2>Add International Order</h2>
            <p className="modal-subtitle">Logged locally — not tied to any ERP order.</p>
          </div>
          <button className="modal-close" onClick={handleClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <label className="login-field">
            <span>Phone Number</span>
            <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} autoFocus required />
          </label>
          <label className="login-field">
            <span>Customer Name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
          </label>
          <label className="login-field">
            <span>Country</span>
            <input type="text" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="e.g. United States" />
          </label>
          <label className="login-field">
            <span>Total Amount</span>
            <input type="number" min="0" step="0.01" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} />
          </label>
          <div className="login-field">
            <span>Product</span>
            <div className="international-order-product-picker">
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search the ERP catalog by name or SKU"
              />
              <input
                type="number"
                min="1"
                value={productQty}
                onChange={(e) => setProductQty(Math.max(1, Number(e.target.value) || 1))}
                aria-label="Quantity"
                className="international-order-product-qty"
              />
              <button
                type="button"
                className="btn btn-secondary btn-small"
                disabled={!productSearch.trim()}
                onClick={handleAddProduct}
              >
                Add
              </button>
            </div>
            {productSearchLoading && <div className="order-query-existing-title">Searching…</div>}
            {productSuggestions.length > 0 && (
              <div className="international-order-product-suggestions">
                {productSuggestions.map((suggestion) => (
                  <button
                    key={suggestion.id}
                    type="button"
                    className="international-order-product-suggestion"
                    onClick={() => handleSelectProductSuggestion(suggestion)}
                  >
                    <span>{suggestion.name}</span>
                    {suggestion.sku && <span className="order-query-existing-meta">SKU: {suggestion.sku}</span>}
                  </button>
                ))}
              </div>
            )}
            {products.length > 0 && (
              <div className="international-order-product-list">
                {products.map((p, index) => (
                  <div key={index} className="international-order-product-list-row">
                    <span>{p.qty}x {p.name}</span>
                    <button type="button" className="btn btn-secondary btn-small" onClick={() => handleRemoveProduct(index)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <label className="login-field">
            <span>Bill Image or PDF (optional)</span>
            <input type="file" accept="image/*,application/pdf" onChange={handleImageChange} />
          </label>
          {billImage && (
            <div className="order-query-attachment-preview">
              <span>{billImage.name}</span>
              <span className="order-query-attachment-preview-actions">
                {billImagePreviewUrl && (
                  <a href={billImagePreviewUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-small">
                    View
                  </a>
                )}
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  onClick={() => {
                    setBillImage(null);
                    setImageError('');
                  }}
                >
                  Remove
                </button>
              </span>
            </div>
          )}
          {imageError && <div className="login-error">{imageError}</div>}
          <label className="login-field">
            <span>AWB Number (optional)</span>
            <input type="text" value={awbNumber} onChange={(e) => setAwbNumber(e.target.value)} placeholder="Often not known yet" />
          </label>
          <label className="login-field">
            <span>Courier Name (optional)</span>
            <input type="text" value={courierName} onChange={(e) => setCourierName(e.target.value)} placeholder="e.g. DHL, FedEx" />
          </label>
          <label className="login-field">
            <span>Tracking Link (optional)</span>
            <input type="url" value={trackingLink} onChange={(e) => setTrackingLink(e.target.value)} placeholder="https://…" />
          </label>
          {error && <div className="login-error">{error}</div>}
          <div className="follow-up-add-actions">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (billImage ? 'Uploading…' : 'Saving…') : 'Add Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Add/update the AWB number, courier name, and tracking link after the fact
// — the shipment usually isn't booked yet when the order is first logged,
// so this is how they get filled in (or corrected) once they exist.
function EditTrackingModal({ order, onClose, onSubmit }) {
  const [awbNumber, setAwbNumber] = useState(order.awb_number || '');
  const [courierName, setCourierName] = useState(order.courier_name || '');
  const [trackingLink, setTrackingLink] = useState(order.tracking_link || '');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const hasExisting = Boolean(order.awb_number || order.courier_name || order.tracking_link);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await onSubmit({ awbNumber: awbNumber.trim(), courierName: courierName.trim(), trackingLink: trackingLink.trim() });
    } catch (err) {
      setError(err.message || 'Failed to save tracking details.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay-center" onClick={onClose}>
      <div className="modal-panel modal-panel-narrow" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{hasExisting ? 'Edit' : 'Add'} Tracking</h2>
            <p className="modal-subtitle">{order.name || order.phone}</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <label className="login-field">
            <span>AWB Number</span>
            <input type="text" value={awbNumber} onChange={(e) => setAwbNumber(e.target.value)} autoFocus />
          </label>
          <label className="login-field">
            <span>Courier Name</span>
            <input type="text" value={courierName} onChange={(e) => setCourierName(e.target.value)} placeholder="e.g. DHL, FedEx" />
          </label>
          <label className="login-field">
            <span>Tracking Link</span>
            <input
              type="url"
              value={trackingLink}
              onChange={(e) => setTrackingLink(e.target.value)}
              placeholder="https://…"
            />
          </label>
          {error && <div className="login-error">{error}</div>}
          <div className="follow-up-add-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Correcting the order's own details after the fact (phone/name/country/
// product/amount typo, or swapping the bill image) — separate from
// EditTrackingModal above since those fields (AWB/courier/tracking link) are
// filled in later in the order's life, not corrections to what was typed at
// creation. product is a plain text field here (not the Add form's ERP
// catalog picker) since an existing order's product is already a joined
// "qty x name, qty x name" string, not a structured line-item list.
function EditInternationalOrderModal({ order, onClose, onSubmit }) {
  const { authFetch } = useAuth();
  const [phone, setPhone] = useState(order.phone || '');
  const [name, setName] = useState(order.name || '');
  const [country, setCountry] = useState(order.country || '');
  const [totalAmount, setTotalAmount] = useState(order.total_amount || '');
  // Same ERP-catalog picker as AddInternationalOrderModal, seeded by parsing
  // the existing joined "qty x name" string back into line items.
  const [products, setProducts] = useState(() => parseProductString(order.product));
  const [productSearch, setProductSearch] = useState('');
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [productQty, setProductQty] = useState(1);
  const [billImage, setBillImage] = useState(null); // File — replaces the existing bill image only if set
  const [billImagePreviewUrl, setBillImagePreviewUrl] = useState('');
  const [imageError, setImageError] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!billImage) {
      setBillImagePreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(billImage);
    setBillImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [billImage]);

  useEffect(() => {
    const term = productSearch.trim();
    if (!term) {
      setProductSuggestions([]);
      setProductSearchLoading(false);
      return undefined;
    }
    setProductSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await authFetch(`/api/ivr/international-orders/products?search=${encodeURIComponent(term)}`);
        const body = await response.json();
        setProductSuggestions(response.ok ? (body.products || []) : []);
      } catch {
        setProductSuggestions([]);
      } finally {
        setProductSearchLoading(false);
      }
    }, PRODUCT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [productSearch, authFetch]);

  const handleSelectProductSuggestion = (suggestion) => {
    setProductSearch(suggestion.name);
    setProductSuggestions([]);
  };

  const handleAddProduct = () => {
    const trimmedName = productSearch.trim();
    if (!trimmedName) return;
    setProducts((prev) => [...prev, { name: trimmedName, qty: productQty || 1 }]);
    setProductSearch('');
    setProductQty(1);
    setProductSuggestions([]);
  };

  const handleRemoveProduct = (index) => {
    setProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImageChange = (event) => {
    const picked = event.target.files?.[0] || null;
    event.target.value = '';
    if (!picked) return;
    if (!picked.type.startsWith('image/') && !isPdfFile(picked)) {
      setImageError('Bill must be an image or a PDF file');
      return;
    }
    if (picked.size > MAX_BILL_IMAGE_BYTES) {
      setImageError('File is too large — the limit is 25 MB');
      return;
    }
    setImageError('');
    setBillImage(picked);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!phone.trim()) {
      setError('Phone number is required.');
      return;
    }
    if (imageError) {
      setError(imageError);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      let billImageUrl = order.bill_image_url || undefined;
      if (billImage) {
        const formData = new FormData();
        formData.append('file', billImage);
        const uploadResponse = await authFetch('/api/ivr/international-orders/attachment', {
          method: 'POST',
          body: formData,
        });
        const uploadBody = await uploadResponse.json();
        if (!uploadResponse.ok) throw new Error(uploadBody?.error || 'Bill image upload failed');
        billImageUrl = uploadBody.url;
      }
      await onSubmit({
        phone: phone.trim(),
        name: name.trim(),
        country: country.trim(),
        totalAmount: totalAmount.trim(),
        product: products.map((p) => `${p.qty}x ${p.name}`).join(', '),
        billImageUrl,
      });
    } catch (err) {
      setError(err.message || 'Failed to save this order.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay-center">
      <div className="modal-panel modal-panel-narrow">
        <div className="modal-header">
          <div>
            <h2>Edit International Order</h2>
            <p className="modal-subtitle">{order.name || order.phone}</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <label className="login-field">
            <span>Phone Number</span>
            <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} autoFocus required />
          </label>
          <label className="login-field">
            <span>Customer Name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
          </label>
          <label className="login-field">
            <span>Country</span>
            <input type="text" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="e.g. United States" />
          </label>
          <label className="login-field">
            <span>Total Amount</span>
            <input type="number" min="0" step="0.01" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} />
          </label>
          <div className="login-field">
            <span>Product</span>
            <div className="international-order-product-picker">
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search the ERP catalog by name or SKU"
              />
              <input
                type="number"
                min="1"
                value={productQty}
                onChange={(e) => setProductQty(Math.max(1, Number(e.target.value) || 1))}
                aria-label="Quantity"
                className="international-order-product-qty"
              />
              <button
                type="button"
                className="btn btn-secondary btn-small"
                disabled={!productSearch.trim()}
                onClick={handleAddProduct}
              >
                Add
              </button>
            </div>
            {productSearchLoading && <div className="order-query-existing-title">Searching…</div>}
            {productSuggestions.length > 0 && (
              <div className="international-order-product-suggestions">
                {productSuggestions.map((suggestion) => (
                  <button
                    key={suggestion.id}
                    type="button"
                    className="international-order-product-suggestion"
                    onClick={() => handleSelectProductSuggestion(suggestion)}
                  >
                    <span>{suggestion.name}</span>
                    {suggestion.sku && <span className="order-query-existing-meta">SKU: {suggestion.sku}</span>}
                  </button>
                ))}
              </div>
            )}
            {products.length > 0 && (
              <div className="international-order-product-list">
                {products.map((p, index) => (
                  <div key={index} className="international-order-product-list-row">
                    <span>{p.qty}x {p.name}</span>
                    <button type="button" className="btn btn-secondary btn-small" onClick={() => handleRemoveProduct(index)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <label className="login-field">
            <span>Bill Image or PDF (optional)</span>
            <input type="file" accept="image/*,application/pdf" onChange={handleImageChange} />
          </label>
          {billImage ? (
            <div className="order-query-attachment-preview">
              <span>{billImage.name}</span>
              <span className="order-query-attachment-preview-actions">
                {billImagePreviewUrl && (
                  <a href={billImagePreviewUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-small">
                    View
                  </a>
                )}
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  onClick={() => {
                    setBillImage(null);
                    setImageError('');
                  }}
                >
                  Remove
                </button>
              </span>
            </div>
          ) : order.bill_image_url ? (
            <div className="order-query-attachment-preview">
              <span>Current bill {isPdfUrl(order.bill_image_url) ? 'PDF' : 'image'}</span>
              <a href={order.bill_image_url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-small">
                View
              </a>
            </div>
          ) : null}
          {imageError && <div className="login-error">{imageError}</div>}
          {error && <div className="login-error">{error}</div>}
          <div className="follow-up-add-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (billImage ? 'Uploading…' : 'Saving…') : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Renders the bill link/thumbnail for one row. isPdfUrl catches the normal
// case up front (no flash of a broken image), but a URL saved before the
// server started deriving its extension from the verified mimetype (see
// server/ivr/routes.js's MIME_UPLOAD_EXTENSIONS) can be a PDF with no
// ".pdf" in its URL at all — for those, the <img>'s onError falls back to
// the same file-icon link, so an old row self-heals without a DB backfill.
function BillCell({ url }) {
  const [imgFailed, setImgFailed] = useState(false);
  if (!url) return '-';
  if (isPdfUrl(url) || imgFailed) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="international-order-bill-pdf" title="View bill file">
        <FileIcon width={20} height={20} />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <img src={url} alt="Bill" className="international-order-bill-thumb" onError={() => setImgFailed(true)} />
    </a>
  );
}

// Manually-logged international orders — a flat add-and-list table, no
// ERP/Shopify source and no status workflow (see CLAUDE.md). Fed entirely by
// agents via the Add Order form above; server/ivr/db.js's
// ivr_international_orders is the only source of truth.
function InternationalOrders() {
  const { authFetch } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Reopens automatically if a draft survived an accidental sidebar-nav
  // unmount (see DRAFT_STORAGE_KEY above) so the agent lands back on their
  // in-progress order instead of an empty page.
  const [showAddForm, setShowAddForm] = useState(() => Boolean(loadOrderDraft()));
  const [deletingId, setDeletingId] = useState(null);
  const [editingTrackingOrder, setEditingTrackingOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);

  const fetchOrders = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await authFetch('/api/ivr/international-orders');
      const body = await response.json();
      if (!response.ok) {
        if (!silent) setOrders([]);
        setError(body?.error || 'Failed to fetch international orders.');
        return;
      }
      setError('');
      setOrders(Array.isArray(body?.orders) ? body.orders : []);
    } catch (err) {
      if (!silent) setOrders([]);
      setError(err.message || 'Failed to fetch international orders.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(() => fetchOrders({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return orders;
    return orders.filter((order) =>
      [order.phone, order.name, order.country, order.product]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(term))
    );
  }, [orders, search]);

  useEffect(() => {
    setPage(1);
  }, [search, orders.length]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const pageRows = filteredOrders.slice((page - 1) * pageSize, page * pageSize);

  // Sr No — a stable serial number, oldest order = 1, each new order simply
  // gets the next number up (e.g. 5, 6, ...). Built from the full unfiltered
  // `orders` list (not filteredOrders/pageRows) so it never resets/reshuffles
  // under a search or when paging — only oldest-to-newest position matters,
  // and order.id (an auto-increment PK) already reflects that.
  const serialNumberByOrderId = useMemo(() => {
    const map = new Map();
    [...orders]
      .sort((a, b) => a.id - b.id)
      .forEach((order, index) => map.set(order.id, index + 1));
    return map;
  }, [orders]);

  const handleAdd = async (fields) => {
    const response = await authFetch('/api/ivr/international-orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error(body?.error || 'Failed to save this order.');
    setOrders((prev) => [body, ...prev]);
    setShowAddForm(false);
  };

  const handleDelete = async (order) => {
    if (!window.confirm(`Delete the international order for ${order.name || order.phone}?`)) return;
    setDeletingId(order.id);
    try {
      const response = await authFetch(`/api/ivr/international-orders/${order.id}`, { method: 'DELETE' });
      const body = await response.json().catch(() => null);
      if (!response.ok) throw new Error(body?.error || 'Failed to delete this order.');
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
    } catch (err) {
      setError(err.message || 'Failed to delete this order.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaveTracking = async ({ awbNumber, courierName, trackingLink }) => {
    const response = await authFetch(
      `/api/ivr/international-orders/${editingTrackingOrder.id}/tracking`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ awbNumber, courierName, trackingLink }),
      }
    );
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error(body?.error || 'Failed to save tracking details.');
    setOrders((prev) => prev.map((o) => (o.id === body.id ? body : o)));
    setEditingTrackingOrder(null);
  };

  const handleSaveEdit = async (fields) => {
    const response = await authFetch(`/api/ivr/international-orders/${editingOrder.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error(body?.error || 'Failed to save this order.');
    setOrders((prev) => prev.map((o) => (o.id === body.id ? body : o)));
    setEditingOrder(null);
  };

  // Deliver/Undo — a plain manual marker (no courier-tracking API to detect
  // this automatically), just so agents can flag a row once the order's
  // actually reached the customer and have it stand out (green row) in the
  // list.
  const [togglingDeliveredId, setTogglingDeliveredId] = useState(null);
  const handleToggleDelivered = async (order) => {
    setTogglingDeliveredId(order.id);
    try {
      const response = await authFetch(`/api/ivr/international-orders/${order.id}/delivered`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ delivered: !order.delivered }),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) throw new Error(body?.error || 'Failed to update delivery status.');
      setOrders((prev) => prev.map((o) => (o.id === body.id ? body : o)));
    } catch (err) {
      setError(err.message || 'Failed to update delivery status.');
    } finally {
      setTogglingDeliveredId(null);
    }
  };

  return (
    <>
      <div className="incoming-filters-bar">
        <button type="button" className="btn btn-secondary" onClick={() => fetchOrders()} disabled={loading}>
          <RefreshIcon width={14} height={14} />
          Refresh
        </button>
        <div className="search-input-wrap">
          <SearchIcon className="search-input-icon" width={15} height={15} />
          <input
            type="text"
            className="search-input"
            placeholder="Search by number, name, country, or product"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search international orders"
          />
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setShowAddForm(true)}>
          Add Order
        </button>
      </div>

      {error && <div className="call-feedback call-feedback-error">{error}</div>}

      {loading ? (
        <div className="loading-state">Loading…</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sr No</th>
                <th>Phone</th>
                <th>Name</th>
                <th>Country</th>
                <th>Product</th>
                <th>Total Amount</th>
                <th>Bill</th>
                <th>Tracking</th>
                <th>Added By</th>
                <th>Added On</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ textAlign: 'center' }}>
                    {error
                      ? 'Could not load international orders.'
                      : search
                      ? 'No international orders match this search.'
                      : 'No international orders logged yet.'}
                  </td>
                </tr>
              ) : (
                pageRows.map((order) => (
                  <tr key={order.id} className={order.delivered ? 'row-delivered' : ''}>
                    <td>{serialNumberByOrderId.get(order.id)}</td>
                    <td>{order.phone || '-'}</td>
                    <td>{order.name || '-'}</td>
                    <td>{order.country || '-'}</td>
                    <td>{order.product || '-'}</td>
                    <td>{formatAmount(order.total_amount)}</td>
                    <td>
                      <BillCell url={order.bill_image_url} />
                    </td>
                    <td>
                      <div className="cod-actions">
                        {(order.awb_number || order.courier_name || order.tracking_link) && (
                          <span>
                            {order.awb_number && <div className="table-cell-strong">{order.awb_number}</div>}
                            {order.courier_name && <div>{order.courier_name}</div>}
                            {order.tracking_link && (
                              <a href={order.tracking_link} target="_blank" rel="noopener noreferrer">
                                Track
                              </a>
                            )}
                          </span>
                        )}
                        <button
                          type="button"
                          className="btn btn-secondary btn-small"
                          onClick={() => setEditingTrackingOrder(order)}
                        >
                          {order.awb_number || order.courier_name || order.tracking_link ? 'Edit' : 'Add'}
                        </button>
                      </div>
                    </td>
                    <td>{order.agent || '-'}</td>
                    <td>{formatDateTime(order.created_at)}</td>
                    <td>
                      <div className="cod-actions">
                        <button
                          type="button"
                          className={`btn btn-small ${order.delivered ? 'btn-secondary' : 'btn-success'}`}
                          disabled={togglingDeliveredId === order.id}
                          onClick={() => handleToggleDelivered(order)}
                        >
                          {order.delivered ? 'Undo' : 'Deliver'}
                        </button>
                        {order.tracking_link ? (
                          <a
                            href={order.tracking_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary btn-small"
                          >
                            Track
                          </a>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-secondary btn-small"
                            onClick={() => setEditingTrackingOrder(order)}
                          >
                            Add Tracking
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-secondary btn-small"
                          onClick={() => setEditingOrder(order)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger-outline btn-small"
                          disabled={deletingId === order.id}
                          onClick={() => handleDelete(order)}
                        >
                          <TrashIcon width={14} height={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={filteredOrders.length}
      />

      {showAddForm && <AddInternationalOrderModal onClose={() => setShowAddForm(false)} onSubmit={handleAdd} />}
      {editingTrackingOrder && (
        <EditTrackingModal
          order={editingTrackingOrder}
          onClose={() => setEditingTrackingOrder(null)}
          onSubmit={handleSaveTracking}
        />
      )}
      {editingOrder && (
        <EditInternationalOrderModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSubmit={handleSaveEdit}
        />
      )}
    </>
  );
}

export default InternationalOrders;
